import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
type LegalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'privacy' | 'terms';
};
export function LegalModal({
  isOpen,
  onClose,
  initialTab = 'privacy'
}: LegalModalProps) {
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>(initialTab);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-40">
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-41" />

      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200" data-id="element-42">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100" data-id="element-43">
          <div className="flex space-x-6" data-id="element-44">
            <button onClick={() => setActiveTab('privacy')} className={`text-sm font-semibold tracking-wide uppercase transition-colors ${activeTab === 'privacy' ? 'text-rose-600 border-b-2 border-rose-600 pb-1' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-45">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveTab('terms')} className={`text-sm font-semibold tracking-wide uppercase transition-colors ${activeTab === 'terms' ? 'text-rose-600 border-b-2 border-rose-600 pb-1' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-46">
              Términos y Condiciones
            </button>
          </div>
          <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-full transition-colors" data-id="element-47">
            <XIcon className="w-5 h-5" data-id="element-48" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 sm:p-8 text-stone-600 text-sm leading-relaxed space-y-4" data-id="element-49">
          {activeTab === 'privacy' ? <div className="space-y-4" data-id="element-50">
              <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-51">
                Política de Privacidad
              </h3>
              <p data-id="element-52">
                En Reset Hormonal, nos comprometemos a proteger su privacidad.
                Esta política describe cómo recopilamos, usamos y protegemos su
                información personal.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-53">
                1. Recopilación de Información
              </h4>
              <p data-id="element-54">
                Recopilamos información que usted nos proporciona directamente
                al realizar una compra, como su nombre y dirección de correo
                electrónico. No almacenamos información de tarjetas de crédito;
                los pagos son procesados por proveedores seguros de terceros.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-55">
                2. Uso de la Información
              </h4>
              <p data-id="element-56">
                Utilizamos su información para procesar su pedido, entregar el
                producto digital y enviarle actualizaciones relevantes sobre su
                compra. No vendemos ni compartimos sus datos con terceros para
                fines comerciales.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-57">
                3. Seguridad de Datos
              </h4>
              <p data-id="element-58">
                Implementamos medidas de seguridad razonables para proteger su
                información contra el acceso no autorizado, la alteración o la
                destrucción.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-59">4. Cookies</h4>
              <p data-id="element-60">
                Utilizamos cookies esenciales para el funcionamiento del sitio y
                para mejorar su experiencia de navegación.
              </p>
              <p className="italic mt-6 text-xs text-stone-400" data-id="element-61">
                Última actualización: Enero 2024
              </p>
            </div> : <div className="space-y-4" data-id="element-62">
              <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-63">
                Términos y Condiciones
              </h3>
              <p data-id="element-64">
                Al acceder y utilizar este sitio web y comprar nuestros
                productos, usted acepta los siguientes términos y condiciones.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-65">
                1. Propiedad Intelectual
              </h4>
              <p data-id="element-66">
                Todo el contenido, incluyendo textos, gráficos, logotipos y el
                producto digital "Reset Hormonal", es propiedad exclusiva de
                Reset Hormonal y está protegido por leyes de derechos de autor.
                La compra le otorga una licencia personal e intransferible para
                uso privado. Queda prohibida su distribución o reventa.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-67">
                2. Naturaleza del Producto
              </h4>
              <p data-id="element-68">
                Este es un producto digital. La entrega se realiza vía correo
                electrónico inmediatamente después de la compra.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-69">
                3. Descargo de Responsabilidad Médica
              </h4>
              <p data-id="element-70">
                El contenido de esta guía es solo para fines informativos y
                educativos. No sustituye el consejo, diagnóstico o tratamiento
                médico profesional. Siempre busque el consejo de su médico u
                otro proveedor de salud calificado con cualquier pregunta que
                pueda tener sobre una condición médica.
              </p>
              <h4 className="font-bold text-stone-900 mt-4" data-id="element-71">
                4. Política de Reembolso
              </h4>
              <p data-id="element-72">
                Debido a la naturaleza digital del producto, ofrecemos una
                garantía de satisfacción sujeta a las condiciones especificadas
                en la página de venta.
              </p>
              <p className="italic mt-6 text-xs text-stone-400" data-id="element-73">
                Última actualización: Enero 2024
              </p>
            </div>}
        </div>
      </div>
    </div>;
}